/**
 * useSponsors Hook
 *
 * React Query hooks for sponsor operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';

import { sponsorsService, CreateSponsorDto, UpdateSponsorDto } from '../services/sponsors.service';
import { unwrapResponse } from '../types';

import { investmentKeys } from './useInvestments';

import type { Sponsor, SponsorWithType } from '@medsuite/models';

export const sponsorKeys = {
  all: ['sponsors'] as const,
  lists: () => [...sponsorKeys.all, 'list'] as const,
  list: (investmentId: number) => [...sponsorKeys.lists(), investmentId] as const,
};

/**
 * Fetch all sponsors for an investment
 */
export function useSponsors(
  investmentId: number,
  options?: Omit<UseQueryOptions<SponsorWithType[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<SponsorWithType[], Error>({
    queryKey: sponsorKeys.list(investmentId),
    queryFn: async () => {
      const response = await sponsorsService.getAll(investmentId);
      // Extract data from success wrapper: {success: true, data: {investment_id, sponsors, total}, meta: {}}
      const responseData = unwrapResponse<{ sponsors?: SponsorWithType[] }>(response.data);
      // The API returns {investment_id, sponsors, total}, extract the sponsors array
      return responseData.sponsors || [];
    },
    ...options,
  });
}

/**
 * Create new sponsor
 */
export function useCreateSponsor(
  investmentId: number,
  options?: UseMutationOptions<Sponsor, Error, Omit<CreateSponsorDto, 'investment_id'>>
) {
  const queryClient = useQueryClient();

  return useMutation<Sponsor, Error, Omit<CreateSponsorDto, 'investment_id'>>({
    mutationFn: async (data) => {
      const response = await sponsorsService.create(investmentId, data);
      return unwrapResponse<Sponsor>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sponsorKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
    },
    ...options,
  });
}

/**
 * Update sponsor
 */
export function useUpdateSponsor(
  investmentId: number,
  options?: UseMutationOptions<Sponsor, Error, { sponsorId: number; data: UpdateSponsorDto }>
) {
  const queryClient = useQueryClient();

  return useMutation<Sponsor, Error, { sponsorId: number; data: UpdateSponsorDto }>({
    mutationFn: async ({ sponsorId, data }) => {
      const response = await sponsorsService.update(investmentId, sponsorId, data);
      return unwrapResponse<Sponsor>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sponsorKeys.list(investmentId) });
      // Invalidate counts (for "My Investments" count) and lists (for research_owners display)
      queryClient.invalidateQueries({ queryKey: investmentKeys.counts() });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
    },
    ...options,
  });
}

/**
 * Delete sponsor
 */
export function useDeleteSponsor(
  investmentId: number,
  options?: UseMutationOptions<void, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (sponsorId) => {
      await sponsorsService.delete(investmentId, sponsorId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sponsorKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
    },
    ...options,
  });
}
